import React, { useEffect, useRef } from 'react';

const ACCENT = { r: 122, g: 163, b: 193 };
const COLS = 40;
const ROWS = 25;
const SPACING = 35;
const WAVE_SPEED = 0.02;
const WAVE_AMPLITUDE = 12;

const MeshWaveCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationId = useRef<number>(0);
  const time = useRef(0);
  const mouse = useRef({ x: -1000, y: -1000 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const handleMouse = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouse.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const handleMouseLeave = () => { mouse.current = { x: -1000, y: -1000 }; };
    canvas.addEventListener('mousemove', handleMouse);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    const getPoint = (col: number, row: number, w: number, h: number) => {
      const offsetX = (w - (COLS - 1) * SPACING) / 2;
      const offsetY = (h - (ROWS - 1) * SPACING) / 2;
      const baseX = offsetX + col * SPACING;
      const baseY = offsetY + row * SPACING;

      // Wave displacement
      const waveX = Math.sin(time.current + col * 0.3 + row * 0.2) * WAVE_AMPLITUDE;
      const waveY = Math.cos(time.current * 0.8 + col * 0.2 + row * 0.3) * WAVE_AMPLITUDE * 0.6;

      // Mouse ripple
      const mx = mouse.current.x;
      const my = mouse.current.y;
      const dx = baseX - mx;
      const dy = baseY - my;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const rippleRadius = 200;
      let rippleX = 0;
      let rippleY = 0;
      if (dist < rippleRadius && dist > 0) {
        const force = (1 - dist / rippleRadius) * 25;
        rippleX = (dx / dist) * force;
        rippleY = (dy / dist) * force;
      }

      return {
        x: baseX + waveX + rippleX,
        y: baseY + waveY + rippleY,
        // Depth for pseudo-3D: use wave displacement as z
        z: Math.sin(time.current * 0.5 + col * 0.15 + row * 0.15) * 0.5 + 0.5,
      };
    };

    const draw = () => {
      const { width: w, height: h } = canvas;
      time.current += WAVE_SPEED;

      ctx.fillStyle = '#0a0a0a';
      ctx.fillRect(0, 0, w, h);

      // Draw triangular mesh
      for (let row = 0; row < ROWS - 1; row++) {
        for (let col = 0; col < COLS - 1; col++) {
          const p1 = getPoint(col, row, w, h);
          const p2 = getPoint(col + 1, row, w, h);
          const p3 = getPoint(col, row + 1, w, h);
          const p4 = getPoint(col + 1, row + 1, w, h);

          const avgZ1 = (p1.z + p2.z + p3.z) / 3;
          const avgZ2 = (p2.z + p3.z + p4.z) / 3;

          // Triangle 1
          ctx.strokeStyle = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},${0.05 + avgZ1 * 0.15})`;
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.moveTo(p1.x, p1.y);
          ctx.lineTo(p2.x, p2.y);
          ctx.lineTo(p3.x, p3.y);
          ctx.closePath();
          ctx.stroke();

          // Subtle fill on brighter triangles
          if (avgZ1 > 0.6) {
            ctx.fillStyle = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},${0.02 * avgZ1})`;
            ctx.fill();
          }

          // Triangle 2
          ctx.strokeStyle = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},${0.05 + avgZ2 * 0.15})`;
          ctx.beginPath();
          ctx.moveTo(p2.x, p2.y);
          ctx.lineTo(p4.x, p4.y);
          ctx.lineTo(p3.x, p3.y);
          ctx.closePath();
          ctx.stroke();

          if (avgZ2 > 0.6) {
            ctx.fillStyle = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},${0.02 * avgZ2})`;
            ctx.fill();
          }
        }
      }

      // Draw vertex dots
      for (let row = 0; row < ROWS; row++) {
        for (let col = 0; col < COLS; col++) {
          const p = getPoint(col, row, w, h);
          const brightness = 0.1 + p.z * 0.4;
          const r = 0.5 + p.z * 1.5;

          ctx.fillStyle = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},${brightness})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
          ctx.fill();

          // Glow on brightest points
          if (p.z > 0.8) {
            ctx.shadowColor = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},0.4)`;
            ctx.shadowBlur = 4;
            ctx.beginPath();
            ctx.arc(p.x, p.y, r + 1, 0, Math.PI * 2);
            ctx.fill();
            ctx.shadowBlur = 0;
          }
        }
      }

      animationId.current = requestAnimationFrame(draw);
    };

    animationId.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animationId.current);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', handleMouse);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full z-0" />;
};

export default MeshWaveCanvas;
